import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  MapPin,
  FileBarChart,
  ClipboardList,
  Package,
  TrendingUp,
  Lock,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  Upload,
  FileDown,
  FileSpreadsheet,
  CheckCircle,
  ChevronLeft,
  ArrowUpDown,
  FileText,
  LogOut,
  Pencil,
  ChevronDown,
  Users,
  BarChart3,
  Check,
  Activity,
  Cpu,
  FolderKanban,
  TrendingDown,
  MoreHorizontal,
} from 'lucide-react';
import Card3D from './Card3D.js';
import AnimatedNumber from './AnimatedNumber.js';
import useIntro from './useIntro.js';
import useTheme from './useTheme.js';
import { SALES_DEPARTMENT_TOOL_MENU_ITEMS, SALES_DEPARTMENT_TOOL_META, getSalesDepartmentToolsMeta } from '../SalesDepartmentTools/constants.js';
import MapSubmenu from '../SalesDepartmentTools/MapSubmenu.js';
import SalesPerformanceSubmenu from '../SalesDepartmentTools/SalesPerformanceSubmenu.js';
import BlueprintSubmenu from '../SalesDepartmentTools/BlueprintSubmenu.js';
import FocusProductsSubmenu from '../SalesDepartmentTools/FocusProductsSubmenu.js';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const TAB_ICON_MAP = {
  'project-details': LayoutDashboard,
  'project-details-map': MapPin,
  'project-details-info': FileBarChart,
  'project-details-blueprint': ClipboardList,
  'project-details-focus-products': Package,
};

function PesoSign({ size = 20, style, ...rest }) {
  return (
    <span
      {...rest}
      style={{
        fontSize: typeof size === 'number' ? size : 20,
        lineHeight: 1,
        fontWeight: 800,
        ...(style || {}),
      }}
    >
      ₱
    </span>
  );
}

const formatPeso = (n) => {
  if (n == null || isNaN(n)) return '₱0';
  return '₱' + Number(n).toLocaleString('en-PH', { maximumFractionDigits: 0 });
};

const OVERVIEW_TABS = [
  { id: 'project-details', label: 'Overview', icon: LayoutDashboard },
  { id: 'project-details-map', label: 'MAP', icon: MapPin },
  { id: 'project-details-info', label: 'Sales Performance Report', icon: FileBarChart },
  { id: 'project-details-blueprint', label: 'Blue Print', icon: ClipboardList },
  { id: 'project-details-focus-products', label: 'Focus Products', icon: Package },
];

function SalesoraIntroCurtain({ show, isLight }) {
  const accent = isLight ? '#2563eb' : '#facc15';
  const accentSoft = isLight ? 'rgba(37,99,235,0.78)' : 'rgba(250,204,21,0.82)';
  const pageBg = isLight ? '#ffffff' : '#0f172a';
  const pageBgGradient = isLight
    ? 'radial-gradient(1200px 800px at 12% -10%, rgba(219,234,254,0.95) 0%, rgba(255,255,255,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(254,249,195,0.9) 0%, rgba(255,255,255,0) 55%), linear-gradient(135deg,#eff6ff 0%, #ffffff 45%, #fefce8 100%)'
    : undefined;
  const titleClass = isLight
    ? 'mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900'
    : 'mt-6 font-display text-3xl font-semibold tracking-tight text-white';
  const captionClass = isLight
    ? 'mt-3 font-display text-xs text-slate-500 italic'
    : 'mt-3 font-display text-xs italic';
  const dividerBg = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.08)';
  const tileCls = isLight
    ? 'relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white'
    : 'relative grid size-20 place-items-center rounded-[28px] bg-white p-2.5 border border-white/10';
  const tileStyle = isLight
    ? {
        borderColor: 'rgba(15,23,42,0.08)',
        boxShadow: '0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)',
      }
    : {
        boxShadow:
          '0 24px 60px -18px rgba(250,204,21,0.35), 0 0 0 1px rgba(250,204,21,0.15) inset',
      };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="salesora-intro-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: 'blur(10px)' }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="fixed inset-0 z-[140] grid place-items-center"
          style={{
            background: pageBgGradient || pageBg,
            backgroundColor: pageBg,
          }}
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={titleClass}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              iSource Salesora
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: accentSoft }}
            >
              Sales Department Tools
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: dividerBg }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={captionClass}
              style={{ color: isLight ? 'rgba(15,23,42,0.55)' : accentSoft }}
            >
              Loading sales data and tools…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const sortBluePrintRows = (rows, mode) => {
  const sortedRows = [...rows];
  switch (mode) {
    case 1:
      return sortedRows.sort((a, b) => (a.account_name || '').localeCompare(b.account_name || ''));
    case 2:
      return sortedRows.sort((a, b) => (a.machines || '').localeCompare(b.machines || ''));
    case 3:
      return sortedRows.sort((a, b) => (parseFloat(a.quantity) || 0) - (parseFloat(b.quantity) || 0));
    case 4:
      return sortedRows.sort((a, b) => (parseFloat(b.total_amount) || 0) - (parseFloat(a.total_amount) || 0));
    default:
      return sortedRows;
  }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);

export default function SalesoraPage({ axiosInstance, user = {} }) {
  const { isLight } = useTheme();
  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1400);
    return () => clearTimeout(t);
  }, []);
  const isIntro = useIntro();

  const hasSalesAccess =
    user?.permissions?.project_details_access || user?.email === 'vinsimon23@gmail.com';

  const stagger = (i) =>
    isIntro
      ? { className: 'stagger-in', style: { '--i': i } }
      : { className: '', style: undefined };

  const [activeTab, setActiveTabRaw] = useState('project-details');

  // Sales Accounts state
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [selectedSalesAccount, setSelectedSalesAccount] = useState(null);
  const [showAddSalesDropdown, setShowAddSalesDropdown] = useState(false);
  const [targetAccount, setTargetAccount] = useState(null);
  const [targetAmount, setTargetAmount] = useState('');
  const [isSetTargetDialogOpen, setIsSetTargetDialogOpen] = useState(false);
  const [manualInputIndices, setManualInputIndices] = useState(new Set());
  const [editingTransactionIndex, setEditingTransactionIndex] = useState(null);

  // Focus Products state
  const [focusProducts, setFocusProducts] = useState([]);
  const [focusProductFilter, setFocusProductFilter] = useState('');
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newFocusProduct, setNewFocusProduct] = useState({
    brand: '',
    model: '',
    category: '',
  });
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] = useState(false);
  const [focusProductSelectionFilter, setFocusProductSelectionFilter] = useState('');

  // Map state
  const csvInputRef = useRef(null);
  const [mapAccounts, setMapAccounts] = useState([]);
  const [mapSearchTerm, setMapSearchTerm] = useState('');
  const [mapMonthFilter, setMapMonthFilter] = useState('All');
  const [mapStatusFilter, setMapStatusFilter] = useState('All');
  const [showMapAccountModal, setShowMapAccountModal] = useState(false);
  const [mapAccountData, setMapAccountData] = useState({
    account_name: '',
    location: '',
    status: 'Pending',
    scheduled_visit: '',
    notes: '',
  });
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Blue Prints state
  const [bluePrints, setBluePrints] = useState([]);
  const [selectedBluePrintAccount, setSelectedBluePrintAccount] = useState(null);
  const [bluePrintRows, setBluePrintRows] = useState([]);
  const [bluePrintSortMode, setBluePrintSortMode] = useState(0);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState(['']);
  const [isMachineSelectionModalOpen, setIsMachineSelectionModalOpen] = useState(false);
  const [showMachineModal, setShowMachineModal] = useState(false);
  const [currentMachineRowIndex, setCurrentMachineRowIndex] = useState(null);
  const [machineSearchQuery, setMachineSearchQuery] = useState('');
  const [isManualInputMode, setIsManualInputMode] = useState(false);
  const [manualMachineName, setManualMachineName] = useState('');

  // Employee list
  const [employees, setEmployees] = useState([]);
  const [admins, setAdmins] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    if (!hasSalesAccess || !axiosInstance) return;

    const loadData = async () => {
      try {
        const [salesRes, focusRes, mapRes, bpRes] = await Promise.allSettled([
          axiosInstance.get('/sales-accounts').catch(() => ({ data: [] })),
          axiosInstance.get('/focus-products').catch(() => ({ data: [] })),
          axiosInstance.get('/map-accounts').catch(() => ({ data: [] })),
          axiosInstance.get('/blue-prints').catch(() => ({ data: [] })),
        ]);

        if (salesRes.status === 'fulfilled' && salesRes.value?.data) {
          setSalesAccounts(Array.isArray(salesRes.value.data) ? salesRes.value.data : []);
        }
        if (focusRes.status === 'fulfilled' && focusRes.value?.data) {
          setFocusProducts(Array.isArray(focusRes.value.data) ? focusRes.value.data : []);
        }
        if (mapRes.status === 'fulfilled' && mapRes.value?.data) {
          setMapAccounts(Array.isArray(mapRes.value.data) ? mapRes.value.data : []);
        }
        if (bpRes.status === 'fulfilled' && bpRes.value?.data) {
          setBluePrints(Array.isArray(bpRes.value.data) ? bpRes.value.data : []);
        }
      } catch (err) {
        console.warn('[SalesoraPage] Failed to fetch initial data:', err?.message || err);
      }
    };

    loadData();
  }, [axiosInstance, hasSalesAccess]);

  // Fetch employees/admins for reference
  useEffect(() => {
    if (!axiosInstance) return;
    const loadEmployees = async () => {
      try {
        const allRes = await axiosInstance.get('/users/all').catch(() => ({ data: [] }));
        const allUsers = Array.isArray(allRes.data) ? allRes.data : [];
        setEmployees(allUsers.filter((u) => u.role === 'employee' && !u.is_super_admin));
        setAdmins(allUsers.filter((u) => u.role === 'admin' || !!u.is_super_admin));
      } catch {
        /* noop */
      }
    };
    loadEmployees();
  }, [axiosInstance]);

  // Derived: filtered map accounts
  const filteredMapAccounts = React.useMemo(() => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return mapAccounts.filter((acc) => {
      if (mapSearchTerm) {
        const term = mapSearchTerm.toLowerCase();
        const inName = (acc.account_name || '').toLowerCase().includes(term);
        const inLoc = (acc.location || '').toLowerCase().includes(term);
        const inNotes = (acc.notes || '').toLowerCase().includes(term);
        if (!inName && !inLoc && !inNotes) return false;
      }
      if (mapMonthFilter !== 'All') {
        const dateStr = acc.scheduled_visit || acc.visit_date;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return false;
        if (months[d.getMonth()] !== mapMonthFilter) return false;
      }
      if (mapStatusFilter !== 'All') {
        if ((acc.status || 'Pending') !== mapStatusFilter) return false;
      }
      return true;
    });
  }, [mapAccounts, mapSearchTerm, mapMonthFilter, mapStatusFilter]);

  // Handlers
  const handleTransactionChange = useCallback((accountIdx, txIdx, field, value) => {
    setSalesAccounts((prev) => {
      const next = [...prev];
      if (!next[accountIdx]?.transactions) return prev;
      next[accountIdx] = {
        ...next[accountIdx],
        transactions: next[accountIdx].transactions.map((tx, i) =>
          i === txIdx ? { ...tx, [field]: value } : tx
        ),
      };
      return next;
    });
  }, []);

  const saveTransaction = useCallback(async (accountIdx, txIdx) => {
    try {
      const account = salesAccounts[accountIdx];
      const tx = account?.transactions?.[txIdx];
      if (!tx) return;
      toast.success('Transaction saved locally');
      setManualInputIndices((prev) => {
        const next = new Set(prev);
        next.delete(`${accountIdx}-${txIdx}`);
        return next;
      });
      setEditingTransactionIndex(null);
    } catch (err) {
      toast.error('Failed to save transaction');
    }
  }, [salesAccounts]);

  const handleManualSave = useCallback((accountIdx, txIdx) => {
    saveTransaction(accountIdx, txIdx);
  }, [saveTransaction]);

  const handleAddTransaction = useCallback((accountIdx) => {
    setSalesAccounts((prev) => {
      const next = [...prev];
      if (!next[accountIdx]) return prev;
      const txs = next[accountIdx].transactions || [];
      next[accountIdx] = {
        ...next[accountIdx],
        transactions: [
          ...txs,
          {
            sales_invoice_date: new Date().toISOString().slice(0, 10),
            particular: '',
            dr_no: '',
            si_no: '',
            net_sales: 0,
            vat: 0,
            unit: '',
            focus_product: '',
            sales_engineer: '',
          },
        ],
      };
      return next;
    });
  }, []);

  const handleDeleteTransaction = useCallback((accountIdx, txIdx) => {
    setSalesAccounts((prev) => {
      const next = [...prev];
      if (!next[accountIdx]?.transactions) return prev;
      next[accountIdx] = {
        ...next[accountIdx],
        transactions: next[accountIdx].transactions.filter((_, i) => i !== txIdx),
      };
      return next;
    });
    toast.success('Transaction removed');
  }, []);

  const handleExportPDF = useCallback(async (account) => {
    toast.info('PDF export triggered for: ' + (account?.full_name || 'Account'));
  }, []);

  const handleExportExcel = useCallback(async (account) => {
    toast.info('Excel export triggered for: ' + (account?.full_name || 'Account'));
  }, []);

  const handleExportAllPDF = useCallback(async () => {
    toast.info('All accounts PDF export triggered');
  }, []);

  // Map handlers
  const handleSaveMapAccount = useCallback(async () => {
    try {
      if (editingAccountId) {
        toast.success('Map account updated');
      } else {
        const newAcc = { ...mapAccountData, id: Date.now() };
        setMapAccounts((prev) => [...prev, newAcc]);
        toast.success('Map account added');
      }
      setShowMapAccountModal(false);
      setEditingAccountId(null);
      setMapAccountData({
        account_name: '',
        location: '',
        status: 'Pending',
        scheduled_visit: '',
        notes: '',
      });
    } catch {
      toast.error('Failed to save map account');
    }
  }, [mapAccountData, editingAccountId]);

  const handleEditClick = useCallback((acc) => {
    setEditingAccountId(acc.id || acc._id);
    setEditFormData({ ...acc });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    try {
      setMapAccounts((prev) =>
        prev.map((a) =>
          (a.id || a._id) === editingAccountId ? { ...a, ...editFormData } : a
        )
      );
      setEditingAccountId(null);
      setEditFormData({});
      toast.success('Map account updated');
    } catch {
      toast.error('Failed to update');
    }
  }, [editingAccountId, editFormData]);

  const handleCancelEdit = useCallback(() => {
    setEditingAccountId(null);
    setEditFormData({});
  }, []);

  const handleDeleteMapAccount = useCallback((id) => {
    setMapAccounts((prev) => prev.filter((a) => (a.id || a._id) !== id));
    toast.success('Map account deleted');
  }, []);

  const handleExportMapExcel = useCallback(() => toast.info('Map Excel export triggered'), []);
  const handleExportMapPDF = useCallback(() => toast.info('Map PDF export triggered'), []);
  const handleExportMapCSV = useCallback(() => toast.info('Map CSV export triggered'), []);
  const handleImportMapCSV = useCallback((e) => {
    toast.info('Map CSV import triggered');
    if (csvInputRef.current) csvInputRef.current.value = '';
  }, []);

  // Blue Print handlers
  const handleDeleteBluePrint = useCallback((id) => {
    setBluePrints((prev) => prev.filter((b) => (b.id || b._id) !== id));
    setBluePrintRows((prev) => prev.filter((r) => (r.blueprint_id || r.id) !== id));
    toast.success('BluePrint deleted');
  }, []);

  const handleOpenMachineSelection = useCallback((rowIdx) => {
    setCurrentMachineRowIndex(rowIdx);
    setIsMachineSelectionModalOpen(true);
  }, []);

  const handleRemoveFilter = useCallback((idx) => {
    setExportFilters((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleAddFilter = useCallback(() => {
    setExportFilters((prev) => [...prev, '']);
  }, []);

  const handleExportFiltered = useCallback(() => {
    toast.info('Filtered export triggered');
    setIsExportModalOpen(false);
  }, []);

  const handleExportAllBluePrintsExcel = useCallback(() => toast.info('All BluePrints Excel export'), []);

  const handleSelectMachine = useCallback((machine) => {
    if (currentMachineRowIndex != null) {
      setBluePrintRows((prev) => {
        const next = [...prev];
        if (next[currentMachineRowIndex]) {
          next[currentMachineRowIndex] = {
            ...next[currentMachineRowIndex],
            machines: machine?.name || manualMachineName || next[currentMachineRowIndex].machines,
          };
        }
        return next;
      });
    }
    setIsMachineSelectionModalOpen(false);
    setIsManualInputMode(false);
    setManualMachineName('');
    setMachineSearchQuery('');
  }, [currentMachineRowIndex, manualMachineName]);

  const handleExportBluePrintExcel = useCallback(() => toast.info('BluePrint Excel export'), []);
  const handleExportBluePrintPDF = useCallback(() => toast.info('BluePrint PDF export'), []);
  const handleExportBluePrintCSV = useCallback(() => toast.info('BluePrint CSV export'), []);
  const handleImportBluePrintCSV = useCallback(() => toast.info('BluePrint CSV import'), []);

  const handleSaveBluePrint = useCallback(async () => {
    toast.success('BluePrint saved locally');
  }, []);

  const handleDeleteBluePrintRow = useCallback((idx) => {
    setBluePrintRows((prev) => prev.filter((_, i) => i !== idx));
    toast.success('Row removed');
  }, []);

  const fetchFocusProducts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/focus-products');
      if (Array.isArray(res.data)) setFocusProducts(res.data);
    } catch {
      /* noop */
    }
  }, [axiosInstance]);

  const fetchBluePrints = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/blue-prints');
      if (Array.isArray(res.data)) setBluePrints(res.data);
    } catch {
      /* noop */
    }
  }, [axiosInstance]);

  const tabs = OVERVIEW_TABS;

  const moduleSubmenuAccess = (user?.module_access?.salesora?.submenu_access) || {};
  const visibleTabs = React.useMemo(() => {
    return tabs.filter((t) => {
      if (moduleSubmenuAccess[t.id]?.visible === false) return false;
      return true;
    });
  }, [tabs, moduleSubmenuAccess]);
  const setActiveTab = useCallback((id) => {
    if (visibleTabs.find((t) => t.id === id)) {
      setActiveTabRaw(id);
    } else if (visibleTabs[0]) {
      setActiveTabRaw(visibleTabs[0].id);
    }
  }, [visibleTabs]);
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const activeExists = visibleTabs.some((t) => t.id === activeTab);
    if (!activeExists) {
      setActiveTabRaw(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const mapRowsCount = useMemo(() => {
    return filteredMapAccounts.length || mapAccounts.length || 0;
  }, [filteredMapAccounts, mapAccounts]);

  const totalSalesPerformance = useMemo(() => {
    let total = 0;
    if (!Array.isArray(salesAccounts)) return 0;
    salesAccounts.forEach((acc) => {
      if (Array.isArray(acc.transactions)) {
        acc.transactions.forEach((tx) => {
          const net = parseFloat(tx.net_sales);
          if (Number.isFinite(net)) total += net;
        });
      }
    });
    return total;
  }, [salesAccounts]);

  const bpAccountsCount = useMemo(() => {
    let total = 0;
    const rows = Array.isArray(bluePrintRows) && bluePrintRows.length ? bluePrintRows : null;
    if (rows) {
      total = rows.length;
    } else if (Array.isArray(bluePrints)) {
      bluePrints.forEach((bp) => {
        total += (bp.items || []).length;
      });
    }
    return total;
  }, [bluePrintRows, bluePrints]);

  const bpTotalMachineQty = useMemo(() => {
    let total = 0;
    const rows = Array.isArray(bluePrintRows) && bluePrintRows.length ? bluePrintRows : null;
    if (rows) {
      rows.forEach((r) => {
        const q = parseFloat(r.quantity) || parseFloat(r.machine_qty) || 0;
        if (Number.isFinite(q)) total += q;
      });
    } else if (Array.isArray(bluePrints)) {
      bluePrints.forEach((bp) => {
        (bp.items || []).forEach((item) => {
          const q = parseFloat(item.quantity) || 0;
          if (Number.isFinite(q)) total += q;
        });
      });
    }
    return total;
  }, [bluePrintRows, bluePrints]);

  const bpTotalAmount = useMemo(() => {
    let total = 0;
    const rows = Array.isArray(bluePrintRows) && bluePrintRows.length ? bluePrintRows : null;
    if (rows) {
      rows.forEach((r) => {
        const a = parseFloat(r.total_amount) || parseFloat(r.amount) || 0;
        if (Number.isFinite(a)) total += a;
      });
    } else if (Array.isArray(bluePrints)) {
      bluePrints.forEach((bp) => {
        (bp.items || []).forEach((item) => {
          const a = parseFloat(item.total_amount) || 0;
          if (Number.isFinite(a)) total += a;
        });
      });
    }
    return total;
  }, [bluePrintRows, bluePrints]);

  const overviewStats = [
    {
      label: 'MAP — Total Clients',
      value: String(mapRowsCount),
      change: mapRowsCount > 0 ? `${mapRowsCount} clients` : 'No data',
      trending: 'up',
      icon: MapPin,
      color: 'from-cyan-400/20 to-sky-500/10',
      iconVar: 'var(--nad-info)',
    },
    {
      label: 'Sales Performance — Total Amount',
      value: formatPeso(totalSalesPerformance),
      change: `${salesAccounts.length || 0} Accounts`,
      trending: 'up',
      icon: PesoSign,
      color: 'from-accent-400/20 to-accent-500/10',
      iconVar: 'var(--nad-accent-txt)',
    },
    {
      label: 'Blue Print — Total Accounts',
      value: String(bpAccountsCount),
      change: 'Active Projects',
      trending: 'up',
      icon: FolderKanban,
      color: 'from-brand-400/20 to-brand-500/10',
      iconVar: 'var(--nad-info)',
    },
    {
      label: 'Blue Print — Total Machine Qty',
      value: String(bpTotalMachineQty),
      change: 'Units deployed',
      trending: 'up',
      icon: Cpu,
      color: 'from-emerald-400/20 to-emerald-500/10',
      iconVar: 'var(--nad-pos)',
    },
    {
      label: 'Blue Print — Total Amount',
      value: formatPeso(bpTotalAmount),
      change: 'Project value',
      trending: 'up',
      icon: BarChart3,
      color: 'from-purple-400/20 to-fuchsia-500/10',
      iconVar: '#a78bfa',
    },
  ];

  if (!hasSalesAccess) {
    return (
      <>
        <SalesoraIntroCurtain show={!minElapsed} isLight={isLight} />
        <div className="space-y-6">
          <div {...stagger(0)}>
            <Card3D className="content-glass rounded-xl p-12 text-center" intensity={6} lift={12}>
              <div
                className="w-16 h-16 rounded-2xl border flex items-center justify-center mx-auto mb-4"
                style={{
                  background:
                    'linear-gradient(to bottom right, rgba(248,113,113,0.20), rgba(250,204,21,0.20))',
                  borderColor: 'var(--nad-b2)',
                }}
              >
                <Lock size={32} style={{ color: 'var(--nad-neg)' }} />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--nad-t1)' }}>
                Salesora Access Restricted
              </h2>
              <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--nad-t3)' }}>
                You do not have permission to access the Sales Department Tools. Please contact your
                administrator to request access.
              </p>
            </Card3D>
          </div>
        </div>
      </>
    );
  }

  const salesDepartmentToolsProps = {
    activeMenuItem: activeTab,
    user,
    axiosInstance,
    employees,
    admins,
    salesAccounts,
    setSalesAccounts,
    selectedSalesAccount,
    setSelectedSalesAccount,
    showAddSalesDropdown,
    setShowAddSalesDropdown,
    targetAccount,
    setTargetAccount,
    targetAmount,
    setTargetAmount,
    isSetTargetDialogOpen,
    setIsSetTargetDialogOpen,
    handleExportAllPDF,
    handleExportPDF,
    handleExportExcel,
    handleTransactionChange,
    saveTransaction,
    handleManualSave,
    handleAddTransaction,
    handleDeleteTransaction,
    manualInputIndices,
    setEditingTransactionIndex,
    setFocusProductSelectionFilter,
    setIsProductSelectionModalOpen,
    focusProducts,
    setFocusProducts,
    focusProductFilter,
    setFocusProductFilter,
    isAddProductModalOpen,
    setIsAddProductModalOpen,
    isEditProductModalOpen,
    setIsEditProductModalOpen,
    editingProduct,
    setEditingProduct,
    newFocusProduct,
    setNewFocusProduct,
    csvInputRef,
    mapSearchTerm,
    setMapSearchTerm,
    mapMonthFilter,
    setMapMonthFilter,
    mapStatusFilter,
    setMapStatusFilter,
    handleExportMapExcel,
    handleExportMapPDF,
    handleExportMapCSV,
    handleImportMapCSV,
    showMapAccountModal,
    setShowMapAccountModal,
    mapAccountData,
    setMapAccountData,
    handleSaveMapAccount,
    filteredMapAccounts,
    editingAccountId,
    editFormData,
    setEditFormData,
    handleSaveEdit,
    handleCancelEdit,
    handleEditClick,
    handleDeleteMapAccount,
    bluePrints,
    selectedBluePrintAccount,
    setSelectedBluePrintAccount,
    bluePrintRows,
    setBluePrintRows,
    bluePrintSortMode,
    setBluePrintSortMode,
    handleDeleteBluePrint,
    isExportModalOpen,
    setIsExportModalOpen,
    exportFilters,
    handleOpenMachineSelection,
    handleRemoveFilter,
    handleAddFilter,
    handleExportFiltered,
    handleExportAllBluePrintsExcel,
    isMachineSelectionModalOpen,
    setIsMachineSelectionModalOpen,
    handleSelectMachine,
    handleExportBluePrintExcel,
    handleExportBluePrintPDF,
    handleExportBluePrintCSV,
    handleImportBluePrintCSV,
    handleSaveBluePrint,
    handleDeleteBluePrintRow,
    showMachineModal,
    setShowMachineModal,
    fetchFocusProducts,
    currentMachineRowIndex,
    setCurrentMachineRowIndex,
    machineSearchQuery,
    setMachineSearchQuery,
    isManualInputMode,
    setIsManualInputMode,
    manualMachineName,
    setManualMachineName,
    fetchBluePrints,
    setActiveMenuItem: setActiveTab,
  };

  const currentMeta = getSalesDepartmentToolsMeta(activeTab, selectedSalesAccount);

  return (
    <>
      <SalesoraIntroCurtain show={!minElapsed} isLight={isLight} />
      <div className="space-y-6">
        <div {...stagger(0)}>
          <Card3D className="content-glass rounded-2xl p-2" intensity={3} lift={6} tilt={false}>
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl border flex items-center justify-center"
                  style={{
                    background:
                      'linear-gradient(to bottom right, rgba(34,197,94,0.20), rgba(59,130,246,0.15))',
                    borderColor: 'var(--nad-b2)',
                  }}
                >
                  <TrendingUp size={20} style={{ color: 'var(--nad-accent-txt)' }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: 'var(--nad-t1)' }}>
                    Salesora — Sales Department Tools
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>
                    {currentMeta?.description ||
                      'Sales pipeline, revenue analytics, client mapping, performance tracking, and blue print management'}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-2 pb-2">
              <div
                className="flex flex-wrap items-center gap-1 p-1 rounded-xl"
                style={{ background: 'var(--nad-s1)' }}
              >
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300`}
                      style={{
                        background: isActive
                          ? 'linear-gradient(to right, rgba(250,204,21,0.18), rgba(59,130,246,0.12))'
                          : 'transparent',
                        color: isActive ? 'var(--nad-accent-txt)' : 'var(--nad-t3)',
                        boxShadow: isActive
                          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 2px rgba(0,0,0,0.08)'
                          : undefined,
                      }}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      <span className="truncate whitespace-nowrap">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Card3D>
        </div>

        <div {...stagger(1)}>
          <div className="space-y-6">
            {activeTab === 'project-details' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {overviewStats.map((stat, i) => {
                  const Icon = stat.icon;
                  const s = stagger(2 + i);
                  return (
                    <div key={stat.label} className={s.className} style={s.style}>
                      <Card3D className="content-glass rounded-xl p-5 group h-full">
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}
                          >
                            <Icon size={20} style={{ color: stat.iconVar }} />
                          </div>
                          <button
                            className="p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                            style={{ color: 'var(--nad-t4)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--nad-t2)'; e.currentTarget.style.background = 'var(--nad-s1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--nad-t4)'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </div>
                        <p className="text-[11px] font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--nad-t3)' }}>
                          {stat.label}
                        </p>
                        <div className="flex items-end justify-between">
                          <AnimatedNumber
                            value={stat.value}
                            delay={500 + i * 110}
                            className="text-2xl font-bold"
                            style={{ color: 'var(--nad-t1)' }}
                          />
                          <div
                            className="flex items-center gap-1 text-xs font-semibold"
                            style={{ color: stat.trending === 'up' ? 'var(--nad-pos)' : 'var(--nad-neg)' }}
                          >
                            {stat.trending === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            <span className="truncate max-w-[100px]">{stat.change}</span>
                          </div>
                        </div>
                      </Card3D>
                    </div>
                  );
                })}
              </div>
            )}
            {activeTab === 'project-details-map' && (
              <MapSubmenu {...salesDepartmentToolsProps} />
            )}
            {(activeTab === 'project-details-info' || activeTab === 'sales-account-details') && (
              <SalesPerformanceSubmenu {...salesDepartmentToolsProps} />
            )}
            {(activeTab === 'project-details-blueprint' || activeTab === 'blueprint-account-details') && (
              <BlueprintSubmenu {...salesDepartmentToolsProps} />
            )}
            {activeTab === 'project-details-focus-products' && (
              <FocusProductsSubmenu {...salesDepartmentToolsProps} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
